/**
 * StorageManager - Safe localStorage wrapper with quota management and error handling
 * Based on BurrowTV-LITE-1local template (line ~9916)
 * 
 * Handles QuotaExceededError gracefully, auto-parses JSON, and provides cleanup utilities
 */

class StorageManagerClass {
  constructor() {
    if (StorageManagerClass.instance) {
      return StorageManagerClass.instance;
    }
    
    // Essential keys that should never be auto-deleted
    this.protectedKeys = [
      'burrowtoken',
      'burrowUserData',
      'isAdmin',
      'burrowerId',
      'playerPreference',
      'viewMode'
    ];
    
    // Track storage usage
    this.usageTracking = true;
    
    StorageManagerClass.instance = this;
  }
  
  /**
   * Safely get item from localStorage
   * Auto-parses JSON and booleans
   * @param {string} key - Storage key
   * @param {*} defaultValue - Default value if key doesn't exist
   * @returns {*} Parsed value or defaultValue
   */
  get(key, defaultValue = null) {
    try {
      const item = localStorage.getItem(key);
      
      if (item === null) {
        return defaultValue;
      }
      
      // Try to parse as JSON
      try {
        return JSON.parse(item);
      } catch {
        // Not JSON - return as string
        // Handle boolean strings
        if (item === 'true') return true;
        if (item === 'false') return false;
        
        return item;
      }
    } catch (error) {
      console.error(`[StorageManager] Error getting key "${key}":`, error);
      return defaultValue;
    }
  }
  
  /**
   * Safely set item in localStorage
   * Auto-stringifies objects and arrays
   * Handles QuotaExceededError by cleaning up old data
   * @param {string} key - Storage key
   * @param {*} value - Value to store (will be JSON.stringify'd if object/array)
   * @returns {boolean} True if successful, false if failed
   */
  set(key, value) {
    try {
      const stringValue = typeof value === 'object' 
        ? JSON.stringify(value) 
        : String(value);
      
      localStorage.setItem(key, stringValue);
      return true;
    } catch (error) {
      // Handle quota exceeded error
      if (error.name === 'QuotaExceededError') {
        console.warn('[StorageManager] Quota exceeded, attempting cleanup...');
        
        // Try to free up space by removing non-essential items
        const freedSpace = this.cleanup();
        
        if (freedSpace) {
          // Try again after cleanup
          try {
            const stringValue = typeof value === 'object' 
              ? JSON.stringify(value) 
              : String(value);
            
            localStorage.setItem(key, stringValue);
            console.log('[StorageManager] Successfully stored after cleanup');
            return true;
          } catch (retryError) {
            console.error('[StorageManager] Still failed after cleanup:', retryError);
            return false;
          }
        } else {
          console.error('[StorageManager] Cleanup failed to free space');
          return false;
        }
      }
      
      console.error(`[StorageManager] Error setting key "${key}":`, error);
      return false;
    }
  }
  
  /**
   * Remove item from localStorage
   * @param {string} key - Storage key
   */
  remove(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`[StorageManager] Error removing key "${key}":`, error);
      return false;
    }
  }
  
  /**
   * Check if key exists in localStorage
   * @param {string} key - Storage key
   * @returns {boolean} True if key exists
   */
  has(key) {
    try {
      return localStorage.getItem(key) !== null;
    } catch (error) {
      console.error(`[StorageManager] Error checking key "${key}":`, error);
      return false;
    }
  }
  
  /**
   * Clear all localStorage data
   * Optionally preserve protected keys
   * @param {boolean} preserveProtected - Keep protected keys (default: true)
   */
  clear(preserveProtected = true) {
    try {
      if (preserveProtected) {
        // Save protected values
        const protectedValues = {};
        this.protectedKeys.forEach(key => {
          const value = localStorage.getItem(key);
          if (value !== null) {
            protectedValues[key] = value;
          }
        });
        
        // Clear everything
        localStorage.clear();
        
        // Restore protected values
        Object.keys(protectedValues).forEach(key => {
          localStorage.setItem(key, protectedValues[key]);
        });
        
        console.log('[StorageManager] Cleared storage (preserved protected keys)');
      } else {
        localStorage.clear();
        console.log('[StorageManager] Cleared all storage');
      }
      
      return true;
    } catch (error) {
      console.error('[StorageManager] Error clearing storage:', error);
      return false;
    }
  }
  
  /**
   * Clean up non-essential localStorage items to free space
   * Removes cache items, temporary data, and old entries
   * @returns {boolean} True if cleanup freed space
   */
  cleanup() {
    try {
      let freedCount = 0;
      
      // Get all keys
      const allKeys = Object.keys(localStorage);
      
      // Remove items by priority (most expendable first)
      const cleanupPatterns = [
        /^cache_/,        // Cache entries
        /^temp_/,         // Temporary data
        /^debug_/,        // Debug data
        /^old_/,          // Old data
        /_cache$/,        // Anything ending in _cache
        /^analytics_/     // Analytics data
      ];
      
      for (const pattern of cleanupPatterns) {
        const keysToRemove = allKeys.filter(key => 
          pattern.test(key) && !this.protectedKeys.includes(key)
        );
        
        keysToRemove.forEach(key => {
          try {
            localStorage.removeItem(key);
            freedCount++;
          } catch (error) {
            console.error(`[StorageManager] Failed to remove ${key}:`, error);
          }
        });
        
        // If we freed some space, stop
        if (freedCount > 0) break;
      }
      
      if (freedCount === 0) {
        // Last resort: remove oldest non-protected items
        const sortedKeys = allKeys
          .filter(key => !this.protectedKeys.includes(key))
          .sort(); // Alphabetical sort as proxy for age
        
        // Remove up to 10 oldest items
        const toRemove = sortedKeys.slice(0, 10);
        toRemove.forEach(key => {
          try {
            localStorage.removeItem(key);
            freedCount++;
          } catch (error) {
            console.error(`[StorageManager] Failed to remove ${key}:`, error);
          }
        });
      }
      
      console.log(`[StorageManager] Cleanup freed ${freedCount} items`);
      return freedCount > 0;
    } catch (error) {
      console.error('[StorageManager] Cleanup failed:', error);
      return false;
    }
  }
  
  /**
   * Get storage usage statistics
   * @returns {object} Storage statistics
   */
  getStats() {
    try {
      const allKeys = Object.keys(localStorage);
      let totalSize = 0;
      const itemSizes = {};
      
      allKeys.forEach(key => {
        const value = localStorage.getItem(key);
        const size = value ? value.length : 0;
        totalSize += size;
        itemSizes[key] = size;
      });
      
      // Sort by size
      const sortedItems = Object.entries(itemSizes)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10); // Top 10
      
      return {
        totalKeys: allKeys.length,
        totalSizeBytes: totalSize,
        totalSizeKB: (totalSize / 1024).toFixed(2),
        totalSizeMB: (totalSize / 1024 / 1024).toFixed(2),
        largestItems: sortedItems,
        protectedKeys: this.protectedKeys
      };
    } catch (error) {
      console.error('[StorageManager] Error getting stats:', error);
      return null;
    }
  }
  
  /**
   * Check if localStorage is available
   * @returns {boolean} True if localStorage is available
   */
  isAvailable() {
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      console.error('[StorageManager] localStorage not available:', error);
      return false;
    }
  }
}

// Create singleton instance
const StorageManager = new StorageManagerClass();

export default StorageManager;
